import { useState, useRef, cloneElement } from 'react'
import Styles from './TodoForm.module.scss'
import cx from 'classnames'
import { TodoFormProps } from './Types'
import { uniq } from 'lodash'
import { DueDatePicker } from 'components/DueDatePicker/DueDatePicker'
import { TodoAssigneePicker } from 'components/TodoAssigneePicker/TodoAssigneePicker'
import { TodoUser } from 'api/clients/TodoClient'
import { reportError } from 'utils/error'
import { ProseInput } from 'common/ProseInput/ProseInput'
import { ProseInputHandle, ProseInputMention, ProseInputProps } from 'common/ProseInput/Types'
import { Button } from 'components/Button/Button'
import { ConditionalWrapper } from 'common/ConditionalWrapper/ConditionalWrapper'
import Collapse from 'react-css-collapse'
import dayjs from 'dayjs'
import { useKeyboard } from '@react-aria/interactions'
import { ProseStateProvider } from 'common/ProseInput/ProseStateContext'
import { ProseState } from 'common/ProseInput/ProseState'
import { ReactComponent as CalendarIcon } from 'assets/icons/calendar16.svg'

const TodoForm = ({
  initialValues,
  onSubmit,
  onCancel,
  onFocus,
  onBlur,
  onTitleChange,
  onDescriptionChange,
  onAssigneeChange,
  onDueDateChange,
  onEscape,
  className,
  showMeta = true,
  showCheckbox = true,
  submitButtonLabel = 'Create',
  titleInputRef,
  descriptionInputRef,
  primaryCta,
  secondaryCta,
  hideActionsOnEmpty,
  clearOnCancel = false,
  footer,
}: TodoFormProps) => {
  const [title, setTitle] = useState(initialValues.title)
  const [htmlTitle, setHtmlTitle] = useState(initialValues.htmlTitle)
  const [description, setDescription] = useState(initialValues.description)
  const [htmlDescription, setHtmlDescription] = useState(initialValues.htmlDescription)
  const [dueDate, setDueDate] = useState(initialValues.dueDate)
  const [assignee, setAssignee] = useState<TodoUser | null>(initialValues.assignee)
  const [mentionedUsersIds, setMentionedUsersIds] = useState<number[]>([])
  const [submitting, setSubmitting] = useState(false)
  let titleRef = useRef<ProseInputHandle>(null)
  if (titleInputRef) {
    titleRef = titleInputRef
  }
  let descriptionRef = useRef<ProseInputHandle>(null)
  if (descriptionInputRef) {
    descriptionRef = descriptionInputRef
  }
  const [proseState] = useState(() => new ProseState())
  const showActions = title !== '' || description !== '' || Boolean(assignee) || Boolean(dueDate)

  const handleFocus = () => {
    onFocus?.()
    return true
  }

  const handleBlur = () => {
    onBlur?.({ title, htmlTitle, description, htmlDescription, dueDate, assignee })
    return true
  }

  const handleSubmit = () => {
    if (!title) {
      return
    }
    setSubmitting(true)
    onSubmit({ title, htmlTitle, description, htmlDescription, dueDate, assignee }, mentionedUsersIds)
      .then(() => {
        titleRef?.current?.clear()
        descriptionRef.current?.clear()
        setTitle(initialValues.title)
        setHtmlTitle(initialValues.htmlTitle)
        setDescription(initialValues.description)
        setHtmlDescription(initialValues.htmlDescription)
        setDueDate(initialValues.dueDate)
        setAssignee(initialValues.assignee)
      })
      .catch((e) => {
        reportError(e)
      })
      .finally(() => setSubmitting(false))
  }

  const handleCancel = () => {
    onCancel({ title, htmlTitle, description, htmlDescription, dueDate, assignee })
    if (clearOnCancel) {
      titleRef?.current?.clear()
      descriptionRef.current?.clear()
      setAssignee(null)
      setDueDate(null)
    }
  }

  const handleTitleChange = (htmlValue: string, plainTextValue: string, mentions: ProseInputMention[]) => {
    const mentionedUsersIds = mentions.filter(({ type }) => type === 'User').map(({ id }) => id as number)
    setMentionedUsersIds((currentIds) => uniq([...currentIds, ...mentionedUsersIds]))
    setHtmlTitle(htmlValue)
    setTitle(plainTextValue)
    onTitleChange?.(plainTextValue, htmlValue, mentionedUsersIds)
  }

  const handleDescriptionChange = (htmlValue: string, plainTextValue: string, mentions: ProseInputMention[]) => {
    const mentionedUsersIds = mentions.filter(({ type }) => type === 'User').map(({ id }) => id as number)
    setMentionedUsersIds((currentIds) => uniq([...currentIds, ...mentionedUsersIds]))
    setHtmlDescription(htmlValue)
    setDescription(plainTextValue)
    onDescriptionChange?.(plainTextValue, htmlValue, mentionedUsersIds)
  }

  const handleAssigneeChange = (value: TodoUser | null) => {
    setAssignee(value)
    onAssigneeChange?.(value)
  }

  const handleDueDateChange = (value: string | null) => {
    setDueDate(value)
    onDueDateChange?.(value)
  }

  const renderSecondaryCta = () => {
    if (!onCancel) {
      return null
    }
    const ctaProps = {
      onPress: handleCancel,
      isDisabled: submitting,
    }

    if (secondaryCta) {
      return cloneElement(secondaryCta, ctaProps)
    }

    return (
      <Button variant="tertiary" {...ctaProps}>
        Cancel
      </Button>
    )
  }

  const renderPrimaryCta = () => {
    if (primaryCta) {
      return cloneElement(primaryCta, { onPress: handleSubmit, isDisabled: submitting || !title })
    }

    return (
      <Button onPress={handleSubmit} isDisabled={submitting || !title}>
        {submitButtonLabel}
      </Button>
    )
  }

  /* -------------------------------------------------------------------------- */
  /*                              Keyboard handlers                             */
  /* -------------------------------------------------------------------------- */

  /**
   * Form handlers: these handlers only trigger behaviours outside of the editor
   * itself, like submitting on enter or blurring on escape. They DO NOT
   * interfere with the editing experience. Editor specific handlers are
   * implemented below.
   */
  const { keyboardProps: formKeyboardHandlers } = useKeyboard({
    onKeyDown: ({ continuePropagation, key, shiftKey }) => {
      if (key === 'Escape') {
        onEscape?.()
        handleBlur()
        continuePropagation()
      }

      if (key === 'Enter' && !shiftKey) {
        handleSubmit()
      }
    },
  })

  /**
   * Editor specific handlers: these handlers are passed down to the prosemirror
   * instance and dictate whether the event should stop or keep going through
   * the chain of handlers attached to the editor.
   * In this case, we return true to stop the editor from inserting a new line,
   * right before submitting the form.
   *
   * See more info here:
   * https://prosemirror.net/docs/ref/#view.EditorProps.handleKeyDown
   */
  const editorHandleKeyDown: ProseInputProps['handleKeyDown'] = (_, { key, shiftKey }) => {
    if (key === 'Enter' && !shiftKey) {
      return true
    }
    return false
  }

  return (
    <ProseStateProvider state={proseState}>
      <div className={className}>
        <div className={Styles.formFields}>
          <div className={Styles.titleInputRow} {...formKeyboardHandlers}>
            <ProseInput
              ref={titleRef}
              className={cx(Styles.input, Styles.titleInput)}
              onChange={handleTitleChange}
              onFocus={handleFocus}
              onBlur={handleBlur}
              initialHtmlString={htmlTitle}
              placeholder="Name task..."
              handleKeyDown={editorHandleKeyDown}
            />
          </div>

          <div className={Styles.divider} />
          <div {...formKeyboardHandlers}>
            <ProseInput
              ref={descriptionRef}
              className={cx(Styles.input, { [Styles.noOffset]: !showCheckbox })}
              onChange={handleDescriptionChange}
              onFocus={handleFocus}
              onBlur={handleBlur}
              initialHtmlString={htmlDescription}
              placeholder="Add description"
              handleKeyDown={editorHandleKeyDown}
            />
          </div>
        </div>

        <ConditionalWrapper
          condition={hideActionsOnEmpty}
          wrapper={(children) => <Collapse isOpen={showActions}>{children}</Collapse>}
        >
          <div className={Styles.footer}>
            <div className={Styles.actions}>
              {showMeta && (
                <>
                  <TodoAssigneePicker
                    value={assignee as TodoUser}
                    labelClassName={cx({
                      [Styles.assigneePicker]: !!assignee,
                    })}
                    onChange={handleAssigneeChange}
                    showLabel
                    hideEmptyLabel
                    size="xs"
                  />

                  <DueDatePicker
                    size="xs"
                    shape="squared"
                    value={dueDate}
                    hideChevron
                    frontIcon={CalendarIcon}
                    onChange={handleDueDateChange}
                    displayText={(date) => dayjs(date).format('MMM D')}
                  />
                </>
              )}
              {footer}
            </div>
            <div className={Styles.actions}>
              {renderSecondaryCta()}
              {renderPrimaryCta()}
            </div>
          </div>
        </ConditionalWrapper>
      </div>
    </ProseStateProvider>
  )
}

export { TodoForm }
